import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { constants } from '@/lib/constants';
import { insertKeys, defaultKeys } from '@/lib/vuexUtils';
import { calcResolvedBalance } from '@/lib/utils';

const localActions = {
  /**
   * Voids the selected transaction if voidable
   * @param {Object} tran - the transaction to void
   * @return {Object} - results of transaction void
   */
  async voidTransaction({ dispatch, rootGetters, rootState }, { tran, memo = '' }) {
    const cashdrawer = rootGetters['auth/cashDrawer'].cashdrawerid;
    const { amount, companyid, creditcarddata, customerid, paymentsiteid, paymenttype, paymenttypeid } = tran;

    const VoidArTran = {
      amount,
      cashdrawer,
      communicatefinance: 'Y',
      companyid,
      creditcarddata,
      customerid,
      memo,
      paymentsiteid,
      paymenttype,
      paymenttypeid,
      transactioncodeid: null,
    };
    rootState.editObject.saveInFlight = true;
    const results = await dispatch('editObject/post',
      { url: '/tg/artran/save_void_transaction', params: { VoidArTran, ReferencedArTran: tran } },
      { root: true },
    );
    rootState.editObject.saveInFlight = false;
    return results;
  },
  /**
   * Retrieves the card token for the selected transaction
   * @param {String} transactionid - transaction id of the transaction to void
   * @return {Object} - card token to the corresponding transaction
   */
  async getCardToken({ dispatch }, { transactionid }) {
    const { EQUALS } = constants;
    const { results } = await dispatch('editObject/post',
      {
        url: '/tg/cardtoken/query',
        params: {
          q: `transactionid-${EQUALS}${transactionid}`,
        },
      },
      { root: true },
    );
    return results;
  },
  /**
   *
   * @param {Object} selectedItem - the Order or ArTran to be refunded
   * @param {Number} customerid - the id of the customer to issue the refund
   * @return {Object[]} - array of cardtokens for the customer
   */
  async getCustomerTokens({ dispatch, rootGetters }, { order, tran, customerid }) {
    const { EQUALS, NOTEQUALS, ISNULL, STARTSWITH, DELIM } = constants;


    let q;
    if (tran && (tran.paymenttype.paymenttypetype === 'BC' || tran.paymenttype.creditcompanyid)) {
      if (tran._archived) {
        q = `and(customerid-${EQUALS}${customerid}${DELIM}approvalcode-${EQUALS}${tran.approvalcode}${DELIM}voidtransactionid-${ISNULL}${DELIM}transactioncodeid-${NOTEQUALS}R${DELIM})`;
      } else {
        q = `transactionid-${EQUALS}${tran.transactionid}${DELIM}`;
      }
    } else if (order) {
      q = `and(customerid-${EQUALS}${customerid}${DELIM}`;
      if (!rootGetters['permissions/isPermPresent']('customerrefundalltokens')) {
        q += `orderid-${STARTSWITH}${order.mainorderid}${DELIM}`;
      }
      q += `voidtransactionid-${ISNULL}${DELIM}transactioncodeid-${NOTEQUALS}R${DELIM})`;
    }
    if (q) {
      const { results } = await dispatch('editObject/query', {
        url: '/tg/cardtoken/query',
        params: {
          q,
          orderby: 'cardtype,cardpan',
          joinloads: 'paymenttype',
          serialize: '_ui_label',
        },
      }, { root: true });
      return results;
    }
    return [];
  },
  /**
   * Retrieves the Telecheck tran for the selected transaction
   * @param {Object} payload - payload containing the transaction id
   * @return {Object} - Telecheck transaction to the corresponding transaction
   */
  async findTelecheckTrans({ dispatch }, payload) {
    const { results } = await dispatch('editObject/query',
      {
        url: '/tg/artran/find_telechecktran',
        params: {
          ...payload,
        },
      },
      { root: true },
    );
    if (results.length && results[0] !== null) {
      let text = '';
      const tran = results[0];
      if (tran.customerid === null) text = 'Selected transaction was not processed through TeleCheck and is thus not available for voiding';
      else if (tran.status === 'Approved' || tran.status === 'Auth Only') text = 'Selected transaction was guaranteed through TeleCheck but was not processed as an electronic payment - void not available';
      else if (tran.status !== 'Committed') text = 'Selected transaction was never committed through TeleCheck - cannot void';
      if (text) {
        dispatch('notify/push', {
          text,
        }, { root: true });
      }
      return results;
    }
    dispatch('notify/push', {
      title: 'Error',
      text: 'Selected transaction was never committed through UTA. Cannot void',
    }, { root: true });
    return results;
  },
  /**
   * Retreives the UTA transaction for the selected transaction
   * @param {Object} payload - payload containing the transaction id
   * @return {Object} - UTA transaction to the corresponding transaction
   */
  async findUtaTrans({ dispatch }, payload) {
    const { results } = await dispatch('editObject/query',
      {
        url: '/tg/artran/find_utatran',
        params: {
          ...payload,
        },
      },
      { root: true },
    );
    if (!results.length || results[0] === null || results[0].status !== 'A') {
      dispatch('notify/push', {
        title: 'Error',
        text: 'Selected transaction was never committed through UTA. Cannot void',
      }, { root: true });
    }
    return results;
  },

  /**
    * Insert keys into each of the rows and call calcResolvedBalance
    * @param {Object[]} history data returned from getHistory
    * @returns {Object[]} new organized results with resolvedbalance prop applied
    */
  organizeOrders({ rootGetters }, history) {
    const isArray = Array.isArray(history);
    let orders = [];
    let trans = [];
    if (!isArray) {
      if (_isEmpty(history)) return history;
      orders = history.orders;
      trans = history.transactions;
    } else {
      const className = _get(history, '[0]._classname');
      if (className === 'Order') orders = history;
      else trans = history;
    }

    const rootMeta = rootGetters['meta/meta'];
    const newestFirst = rootGetters['preferences/getPreference']('NewestArTransFirst');

    for (const order of orders) {
      if (order.transactions && order.transactions.length) {
        for (const tran of order.transactions) {
          insertKeys(defaultKeys, tran, { schema: rootMeta });
        }
        calcResolvedBalance(trans, newestFirst);
      }
    }

    for (const tran of trans) {
      insertKeys(defaultKeys, tran, { schema: rootMeta });
    }

    calcResolvedBalance(trans, newestFirst);
    return history;
  },

};

export default {
  namespaced: true,
  actions: localActions,
};
